import { defineStore } from 'pinia';
import {
  Board,
  BoardFolder,
  CreateBoardFolder,
  UpdateBoardFolder,
  FindBoardFoldersData,
  CreateBoard,
  UpdateBoard,
  UpdateFavoriteBoards,
  FindBoardsData,
  BoardService,
} from '@/api';
import { useOrganizationStore } from './organizationStore';
import { apiRequestHandler } from '@/utils/api';
import {
  PusherBoardFolderCreatedEvent,
  PusherBoardFolderUpdatedEvent,
  PusherBoardPermissionUpdatedEvent,
  PusherBoardCreatedEvent,
  PusherBoardUpdatedEvent,
  PusherBoardArchivedEvent,
} from '@/models/pusher.models';
import { ChannelEvents } from './events';
import { onUnmounted } from 'vue';
import { sortBoardEntities, sortCycles } from '@/utils/sort';
import { getBoardEntityProvider } from '@/utils/boards';

type FolderLoadStatus = {
  folders?: boolean;
  cycles?: boolean;
  boards?: boolean;
};

type FolderLoadStatusMap = Record<BoardFolder['id'] | 'root', FolderLoadStatus>;
export interface BoardStoreState {
  folders: BoardFolder[];
  boards: Board[];
  cycles: Board[];
  favorites: {
    boardFolders: BoardFolder['id'][];
    boards: Board['id'][];
  };
  alreadyLoadedFolderIds: FolderLoadStatusMap; // used so we know if we should show a loader or just load in the background. Null is root
  favoritesLoaded: boolean;

  isSubscribedToAsyncUpdates: boolean;

  currentFolderId: BoardFolder['parentId'];
  selectorFolderId?: BoardFolder['id'];
}

export const useBoardStore = defineStore('board', {
  /****
   STATE
   ****/
  state: (): BoardStoreState => ({
    folders: [],
    boards: [],
    cycles: [],
    favorites: {
      boardFolders: [],
      boards: [],
    },
    alreadyLoadedFolderIds: {},
    favoritesLoaded: false,

    isSubscribedToAsyncUpdates: false,

    currentFolderId: undefined,
    selectorFolderId: undefined,
  }),

  /****
   GETTERS
   ****/
  getters: {
    rootFolders(): BoardFolder[] {
      return sortBoardEntities(this.folders.filter((f) => !f.parentId));
    },

    currentFolder():
      | null
      | (BoardFolder & {
          provider: ReturnType<typeof getBoardEntityProvider>;
          folders: BoardFolder[];
          boards: Board[];
          cycles: Board[];
        }) {
      const folder = this.folders.find((f) => f.id === this.currentFolderId);
      if (!folder) return null;
      const boards = sortBoardEntities(
        this.boards.filter((b) => b.boardFolderId === folder.id)
      );

      const cycles = sortCycles(
        this.cycles.filter((c) => c.boardFolderId === folder.id)
      );
      const folders = sortBoardEntities(
        this.folders.filter((f) => f.parentId === folder.id)
      );

      const provider = getBoardEntityProvider(folder);
      return {
        ...folder,
        folders,
        boards,
        cycles,
        provider,
      };
    },

    selectorFolder():
      | null
      | (BoardFolder & {
          folders: BoardFolder[];
          boards: Board[];
          cycles: Board[];
        }) {
      const folder = this.folders.find((f) => f.id === this.selectorFolderId);
      if (!folder) return null;
      const boards = sortBoardEntities(
        this.boards.filter((b) => b.boardFolderId === folder.id)
      );
      const cycles = sortCycles(
        this.cycles.filter((c) => c.boardFolderId === folder.id)
      ).reverse();
      const folders = sortBoardEntities(
        this.folders.filter((f) => f.parentId === folder.id)
      );
      return {
        ...folder,
        folders,
        boards,
        cycles,
      };
    },

    allCyclesAndBoards(): Board[] {
      return [...this.boards, ...this.cycles];
    },

    favoriteBoardFolders(): BoardFolder[] {
      return this.favorites.boardFolders
        .map((id) => this.folders.find((f) => f.id === id))
        .filter((f) => f) as BoardFolder[];
    },

    favoriteBoards(): Board[] {
      return this.favorites.boards
        .map((id) => this.boards.find((b) => b.id === id))
        .filter((b) => b) as Board[];
    },
  },

  /****
   ACTIONS
   ****/
  actions: {
    storeFolders(folders: BoardFolder[]) {
      // Build a new array to avoid triggering reactivity during the loop
      const updatedFolders = [...this.folders];

      // add/update folders
      folders.forEach((folder) => {
        const index = updatedFolders.findIndex((f) => f.id === folder.id);
        if (index >= 0) {
          updatedFolders[index] = {
            ...folder,
            permissions:
              folder.permissions ?? updatedFolders[index].permissions,
          };
        } else {
          updatedFolders.push(folder);
        }
      });

      // Assign the sorted array all at once to trigger reactivity only once
      this.folders = sortBoardEntities(updatedFolders);
    },

    deleteFolders(folderIds: BoardFolder['id'][]) {
      const folderIdMap = new Map(folderIds.map((id) => [id, true]));
      this.folders = sortBoardEntities(
        this.folders.filter((f) => !folderIdMap.has(f.id))
      );
    },

    storeBoards(boards: Array<Board>) {
      // Build new arrays to avoid triggering reactivity during the loop
      const updatedBoards = [...this.boards];
      const updatedCycles = [...this.cycles];

      // add/update boards
      boards.forEach((board) => {
        const isCycle = board.timeBased;
        if (isCycle) {
          const index = updatedCycles.findIndex((c) => c.id === board.id);
          if (index >= 0) {
            updatedCycles[index] = {
              ...board,
              permissions:
                board.permissions ?? updatedCycles[index].permissions,
            } as Board;
          } else {
            updatedCycles.push(board as Board);
          }
        } else {
          const index = updatedBoards.findIndex((b) => b.id === board.id);
          if (index >= 0) {
            updatedBoards[index] = {
              ...board,
              permissions:
                board.permissions ?? updatedBoards[index].permissions,
            };
          } else {
            updatedBoards.push(board);
          }
        }
      });

      // Assign the sorted arrays all at once to trigger reactivity only once
      this.boards = sortBoardEntities(updatedBoards);
      this.cycles = updatedCycles;

      // update/remove from quickAccess
      // boards.forEach((board) => {
      //   const index = this.quickAccess.boards.findIndex(
      //     (b) => b.board.id === board.id
      //   );
      //   if (index < 0) return;
      //   if (!board.isFavorite) {
      //     this.quickAccess.boards.splice(index, 1);
      //   } else {
      //     this.quickAccess.boards[index].board = board;
      //   }
      // });
    },

    deleteBoards(boardIds: Board['id'][]) {
      const boardIdMap = new Map(boardIds.map((id) => [id, true]));
      this.boards = this.boards.filter((b) => !boardIdMap.has(b.id));
    },

    storeCycles(cycles: Board[]) {
      // Build a new array to avoid triggering reactivity during the loop
      const updatedCycles = [...this.cycles];

      // add/update cycles
      cycles.forEach((cycle) => {
        const index = updatedCycles.findIndex((c) => c.id === cycle.id);
        if (index >= 0) {
          updatedCycles[index] = {
            ...cycle,
            permissions: cycle.permissions ?? updatedCycles[index].permissions,
          };
        } else {
          updatedCycles.push(cycle);
        }
      });

      // Assign the sorted array all at once to trigger reactivity only once
      this.cycles = sortCycles(updatedCycles);
    },

    deleteCycles(cycleIds: Board['id'][]) {
      const cycleIdMap = new Map(cycleIds.map((id) => [id, true]));
      this.cycles = this.cycles.filter((c) => !cycleIdMap.has(c.id));
    },

    setCurrentFolderId(folderId: BoardStoreState['currentFolderId']) {
      this.currentFolderId = folderId;
    },

    setSelectorFolderId(folderId: BoardStoreState['selectorFolderId']) {
      this.selectorFolderId = folderId;
    },

    addAlreadyLoadedFolderId(
      folderId: BoardFolder['id'] | 'root',
      data: {
        folders?: boolean;
        cycles?: boolean;
        boards?: boolean;
      }
    ) {
      this.alreadyLoadedFolderIds[folderId] = {
        ...(this.alreadyLoadedFolderIds[folderId] || {}),
        ...data,
      };
    },

    // ------------------------------------ Board Folders ------------------------------------ //

    async createBoardFolder(data: CreateBoardFolder) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');
      return apiRequestHandler(async () => {
        const response = await BoardService.createBoardFolder<true>({
          path: { organizationId },
          body: data,
        });
        const folder = response.data.data;

        this.storeFolders([folder]);

        return folder;
      });
    },

    async updateBoardFolder(
      boardFolderId: BoardFolder['id'],
      data: UpdateBoardFolder
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');
      return apiRequestHandler(async () => {
        const response = await BoardService.updateBoardFolder<true>({
          path: { organizationId, boardFolderId },
          body: data,
        });
        const folder = response.data.data;

        this.storeFolders([folder]);

        return folder;
      });
    },

    async archiveBoardFolder(boardFolderId: BoardFolder['id']) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');
      return apiRequestHandler(async () => {
        const response = await BoardService.archiveBoardFolder<true>({
          path: { organizationId, boardFolderId },
        });

        const index = this.folders.findIndex((f) => f.id === boardFolderId);
        if (index >= 0) {
          this.folders[index] = {
            ...this.folders[index],
            archived: true,
            // last item in the hierarchy is the current folder and should be set to archived: true
            hierarchy: [
              ...this.folders[index].hierarchy.slice(0, -1),
              {
                ...this.folders[index].hierarchy.slice(-1)[0],
                archived: true,
              },
            ],
          };
        }
        return response;
      });
    },

    async unarchiveBoardFolder(boardFolderId: BoardFolder['id']) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');
      return apiRequestHandler(async () => {
        const response = await BoardService.unarchiveBoardFolder<true>({
          path: { organizationId, boardFolderId },
        });
        const index = this.folders.findIndex((f) => f.id === boardFolderId);
        if (index >= 0) {
          this.folders[index] = {
            ...this.folders[index],
            archived: false,
            // last item in the hierarchy is the current folder and should be set to archived: false
            hierarchy: [
              ...this.folders[index].hierarchy.slice(0, -1),
              {
                ...this.folders[index].hierarchy.slice(-1)[0],
                archived: false,
              },
            ],
          };
        }
        return response;
      });
    },

    async getBoardFolders(data: FindBoardFoldersData['query']) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');
      return apiRequestHandler(async () => {
        const response = await BoardService.findBoardFolders<true>({
          path: { organizationId },
          query: {
            ...data,
            limit: 10000,
            includeArchived: true,
          },
        });
        const folders = response.data.data;

        // When fetching by parentId (or root), remove stale folders that the
        // API no longer returns (e.g. user lost access). Skip for targeted
        // `ids` fetches since those are partial lookups.
        if (!data?.ids) {
          const returnedIds = new Set(folders.map((f) => f.id));
          const parentId = data?.parentId;
          this.folders = this.folders.filter((f) => {
            const inScope = parentId ? f.parentId === parentId : !f.parentId;
            return !inScope || returnedIds.has(f.id);
          });
        }

        this.storeFolders(folders);

        return folders;
      });
    },

    async getBoardFolder(boardFolderId: BoardFolder['id']) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await BoardService.getBoardFolderById<true>({
          path: { organizationId, boardFolderId },
        });
        const folder = response.data.data;

        this.storeFolders([folder]);

        return folder;
      });
    },

    // ------------------------------------ Boards ------------------------------------ //

    async createBoard(data: CreateBoard) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');
      return apiRequestHandler(async () => {
        const response = await BoardService.createBoard<true>({
          path: { organizationId },
          body: data,
        });
        const board = response.data.data;

        this.storeBoards([board]);

        return board;
      });
    },

    async updateBoard(boardId: Board['id'], data: UpdateBoard) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');
      return apiRequestHandler(async () => {
        const response = await BoardService.updateBoard<true>({
          path: { organizationId, boardId },
          body: data,
        });
        const board = response.data.data;

        this.storeBoards([board]);

        return board;
      });
    },

    async deleteBoard(
      boardId: Board['id'],
      moveToBoardId?: Board['id'] | null
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');
      return apiRequestHandler(async () => {
        const response = await BoardService.archiveBoard<true>({
          path: { organizationId, boardId },
          body: { moveToBoardId: moveToBoardId || null },
        });

        const index = this.boards.findIndex((b) => b.id === boardId);
        if (index >= 0) this.boards.splice(index, 1);

        return response.data;
      });
    },

    async getBoards(
      data: FindBoardsData['query'] & { labelContains?: string }
    ) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await BoardService.findBoards<true>({
          path: { organizationId },
          query: {
            ...data,
            labelContains: data?.labelContains,
            endDateLTE: data?.timeBased ? '2050-01-01' : undefined,
            limit: data?.limit || 10000,
          },
        });
        const boards = response.data.data;

        // When fetching by boardFolderId, remove stale boards/cycles in that
        // folder that the API no longer returns (e.g. user lost access).
        // Skip for targeted `ids` fetches since those are partial lookups.
        if (data?.boardFolderId && !data?.ids) {
          const returnedIds = new Set(boards.map((b) => b.id));
          const folderId = data.boardFolderId;
          if (data?.timeBased === true) {
            this.cycles = this.cycles.filter(
              (c) => c.boardFolderId !== folderId || returnedIds.has(c.id)
            );
          } else if (data?.timeBased === false) {
            this.boards = this.boards.filter(
              (b) => b.boardFolderId !== folderId || returnedIds.has(b.id)
            );
          } else {
            // timeBased not specified — prune both
            this.boards = this.boards.filter(
              (b) => b.boardFolderId !== folderId || returnedIds.has(b.id)
            );
            this.cycles = this.cycles.filter(
              (c) => c.boardFolderId !== folderId || returnedIds.has(c.id)
            );
          }
        }

        this.storeBoards(boards);

        return boards;
      });
    },

    async getBoard(boardId: Board['id']) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');

      return apiRequestHandler(async () => {
        const response = await BoardService.getBoardById<true>({
          path: { organizationId, boardId },
        });
        const board = response.data.data;

        this.storeBoards([board]);

        return board;
      });
    },

    // ------------------------------------ Quick Access ------------------------------------ //

    async getBoardFavorites() {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');
      return apiRequestHandler(async () => {
        const response = await BoardService.findFavoriteBoards<true>({
          path: { organizationId },
        });
        const { boardFolders, boards } = response.data.data;
        const boardFetchPromises: Promise<
          Board[] | BoardFolder[] | undefined
        >[] = [];
        if (boardFolders.length) {
          const boardFolderIdsToFetch = boardFolders.filter(
            (id: string) =>
              !this.folders.find((f: { id: string }) => f.id === id)
          );
          boardFetchPromises.push(
            this.getBoardFolders({ ids: boardFolderIdsToFetch })
          );
        }
        if (boards.length) {
          const boardIdsToFetch = boards.filter(
            (id: string) =>
              !this.boards.find((b: { id: string }) => b.id === id)
          );
          boardFetchPromises.push(this.getBoards({ ids: boardIdsToFetch }));
        }
        await Promise.all(boardFetchPromises);
        this.favorites = {
          boardFolders: boardFolders || [],
          boards: boards || [],
        };
        this.favoritesLoaded = true;
        return response.data;
      });
    },

    async updateBoardFavorites(data: UpdateFavoriteBoards) {
      const organizationId = useOrganizationStore().activeOrganization?.id;
      if (!organizationId)
        throw new Error('This action requires an organization to be selected.');
      return apiRequestHandler(async () => {
        const response = await BoardService.updateFavoriteBoards<true>({
          path: { organizationId },
          body: data,
        });
        const { boardFolders, boards } = response.data.data;
        const boardFetchPromises: Promise<
          Board[] | BoardFolder[] | undefined
        >[] = [];
        if (boardFolders?.length) {
          const boardFolderIdsToFetch = boardFolders.filter(
            (id: string) =>
              !this.folders.find((f: { id: string }) => f.id === id)
          );
          boardFetchPromises.push(
            this.getBoardFolders({ ids: boardFolderIdsToFetch })
          );
        }
        if (boards?.length) {
          const boardIdsToFetch = boards.filter(
            (id: string) =>
              !this.boards.find((b: { id: string }) => b.id === id)
          );
          boardFetchPromises.push(this.getBoards({ ids: boardIdsToFetch }));
        }
        await Promise.all(boardFetchPromises);
        this.favorites = {
          boardFolders: boardFolders || [],
          boards: boards || [],
        };
        this.favoritesLoaded = true;
        return response.data;
      });
    },

    // ------------------------------------ pusher ------------------------------------ //

    _subscribeToPusher() {
      if (this.isSubscribedToAsyncUpdates) return;

      this.isSubscribedToAsyncUpdates = true;

      const boardFolderCreatedHandler = (
        event: PusherBoardFolderCreatedEvent
      ) => {
        const { boardFolder } = event.data;
        this.storeFolders([boardFolder]);
      };

      const boardFolderUpdatedHandler = (
        event: PusherBoardFolderUpdatedEvent
      ) => {
        const { boardFolderAfter } = event.data;
        if (!boardFolderAfter) return;
        // Strip permissions - they are user-specific and come from the updater's context.
        // The permission-updated handler re-fetches with the correct user's context.
        this.storeFolders([{ ...boardFolderAfter, permissions: undefined }]);
      };

      const boardCreatedHandler = (event: PusherBoardCreatedEvent) => {
        const { board } = event.data;
        if (!board) return;
        if (board.timeBased) this.storeCycles([board]);
        else this.storeBoards([board]);
      };

      const boardUpdatedHandler = (event: PusherBoardUpdatedEvent) => {
        const { boardAfter } = event.data;
        if (!boardAfter) return;
        // Strip permissions - they are user-specific and come from the updater's context.
        // The permission-updated handler re-fetches with the correct user's context.
        const boardData = { ...boardAfter, permissions: undefined };
        if (boardData.timeBased) this.storeCycles([boardData]);
        else this.storeBoards([boardData]);
      };

      const boardArchivedHandler = (event: PusherBoardArchivedEvent) => {
        const { board } = event.data;
        if (!board) return;
        if (board.timeBased) this.deleteCycles([board.id]);
        else this.deleteBoards([board.id]);
      };

      // Board permission updated handler — re-fetches affected folders and
      // their boards. For folder permission changes the backend sends only
      // the folder IDs; the frontend re-fetches boards per folder which
      // naturally handles access changes and archived filtering.
      // For individual board permission changes, boardsUpdated contains
      // the single board ID.
      const pendingFolderIds = new Set<string>();
      const pendingBoardIds = new Set<string>();
      let permissionFlushTimer: ReturnType<typeof setTimeout> | undefined;

      const flushPermissionUpdates = async () => {
        const folderIds = [...pendingFolderIds];
        const boardIds = [...pendingBoardIds];
        pendingFolderIds.clear();
        pendingBoardIds.clear();

        // Re-fetch affected folders and their boards.
        // getBoards({ boardFolderId }) already prunes stale boards (lost access)
        // and respects archived filtering.
        // getBoardFolders({ ids }) does NOT prune (it's a partial lookup),
        // so we diff the result to detect folders the user lost access to.
        if (folderIds.length > 0) {
          const [foldersResult] = await Promise.all([
            this.getBoardFolders({ ids: folderIds }),
            ...folderIds.map((folderId) =>
              this.getBoards({ boardFolderId: folderId })
            ),
          ]);
          // Remove folders that weren't returned (user lost access)
          const returnedIds = new Set((foldersResult ?? []).map((f) => f.id));
          const lostFolderIds = folderIds.filter((id) => !returnedIds.has(id));
          if (lostFolderIds.length) {
            this.deleteFolders(lostFolderIds);
          }
        }

        // Re-fetch individually updated boards (e.g. single board permission change).
        // getBoards({ ids }) doesn't prune, so diff to detect lost access.
        if (boardIds.length > 0) {
          const result = await this.getBoards({ ids: boardIds });
          const returnedIds = new Set((result ?? []).map((b) => b.id));
          const lostBoardIds = boardIds.filter((id) => !returnedIds.has(id));
          if (lostBoardIds.length) {
            this.deleteBoards(lostBoardIds);
            this.deleteCycles(lostBoardIds);
          }
        }
      };

      const boardPermissionUpdatedHandler = (
        event: PusherBoardPermissionUpdatedEvent
      ) => {
        event.data.boardFoldersUpdated.forEach((id) =>
          pendingFolderIds.add(id)
        );
        event.data.boardsUpdated.forEach((id) => pendingBoardIds.add(id));

        // Debounce — flush 500ms after last event
        clearTimeout(permissionFlushTimer);
        permissionFlushTimer = setTimeout(flushPermissionUpdates, 500);
      };

      ChannelEvents.on('board-folder-created', boardFolderCreatedHandler);
      ChannelEvents.on('board-folder-updated', boardFolderUpdatedHandler);

      ChannelEvents.on('board-created', boardCreatedHandler);
      ChannelEvents.on('board-updated', boardUpdatedHandler);
      ChannelEvents.on('board-archived', boardArchivedHandler);
      ChannelEvents.on(
        'board-permission-updated',
        boardPermissionUpdatedHandler
      );

      onUnmounted(() => {
        ChannelEvents.off('board-folder-created', boardFolderCreatedHandler);
        ChannelEvents.off('board-folder-updated', boardFolderUpdatedHandler);

        ChannelEvents.off('board-created', boardCreatedHandler);
        ChannelEvents.off('board-updated', boardUpdatedHandler);
        ChannelEvents.off('board-archived', boardArchivedHandler);
        ChannelEvents.off(
          'board-permission-updated',
          boardPermissionUpdatedHandler
        );

        this.isSubscribedToAsyncUpdates = false;
      });
    },
  },
});
